import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import { HttpGateway } from '@core/services/http.gateway'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type {
  OperationHandle,
  OperationOwner,
  OperationResult,
  ReadonlyOperationState,
} from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import { FreshnessRepository } from '@core/infra/freshness/freshness.repository'

export type LoadPeopleFailure = ActionFailurePm & Readonly<{ retryable: boolean }>

export type PersonPm = {
  readonly email: string
  // What to show instead of the email. null = the person never set one; callers
  // fall back to the email themselves (see person-identity.helpers.ts).
  readonly displayName: string | null
}

const PeopleListDtoSchema = z.array(z.object({ email: z.string(), displayName: z.string().nullable().default(null) }))

export const LOAD_PEOPLE_OPERATION = defineOperation<null, LoadPeopleFailure>({
  id: operationId('people.load'),
  key: () => operationKey('people'),
  groups: () => [operationGroup('session'), operationGroup('people')],
  pending: { kind: 'join' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 15_000,
    toFailure: () => ({ code: 'temporarily-unavailable', retryable: false }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

/**
 * The people this user may be shown by name, so no form has to ask anyone to
 * remember an email address.
 *
 * One Repository for the whole app rather than one per form: five surfaces ask
 * the same question of the same endpoint, and the answer does not depend on which
 * form is asking. It is a Singleton for the ordinary reason (P-14) — the list
 * outlives any one panel, and every consumer wants the same copy.
 *
 * Being user-scoped is the part that would be easy to miss (P-28): the answer is
 * *this* viewer's, so it must be forgotten at logout or the next user inherits a
 * list of strangers' addresses. Hence IUserScopedState.
 *
 * Note what this is not: an authorization surface. The server narrows the list to
 * what the viewer may see, and every consumer still accepts a typed address —
 * you must be able to share with someone you have not met. Suggestions only.
 */
@injectable()
export class SuggestionsRepository implements UserScopedState {
  peopleListPm: readonly PersonPm[] | null = null

  private readonly dataGateway: HttpGateway
  private readonly freshnessRepository: FreshnessRepository
  private readonly operationOwner: OperationOwner
  private readonly loadOperation: OperationHandle<null, LoadPeopleFailure>

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
    @inject(FreshnessRepository) freshnessRepository: FreshnessRepository,
  ) {
    this.dataGateway = dataGateway
    this.freshnessRepository = freshnessRepository
    this.operationOwner = operationRepository.createOwner({ id: 'suggestions.repository', lifetime: 'session' })
    this.loadOperation = this.operationOwner.register(LOAD_PEOPLE_OPERATION)

    makeObservable(this, {
      peopleListPm: observable.ref,
    })
  }

  get isLoaded(): boolean {
    return this.peopleListPm !== null
  }

  get loadState(): ReadonlyOperationState<LoadPeopleFailure> {
    return this.loadOperation.stateFor(null)
  }

  /**
   * What consumers call. A failure is deliberately quiet: a picker with no
   * suggestions is a plain text field, which still works, so an unreachable
   * directory must not put an error banner on a page the user came to for
   * something else.
   */
  loadIfNeeded = async (): Promise<void> => {
    if (this.isLoaded && this.freshnessRepository.isFresh('suggestions')) return
    await this.load()
  }

  load = async (): Promise<ActionResponsePm> => {
    const result = await this.loadOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/people', { signal, idempotencyKey: null })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = PeopleListDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data', retryable: false })

        return operationSucceeded(
          Object.freeze(
            parsed.data.map((dto): PersonPm => Object.freeze({ email: dto.email, displayName: dto.displayName })),
          ),
        )
      },
      {
        succeeded: (people) => {
          this.peopleListPm = people
          this.freshnessRepository.markLoaded('suggestions')
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.peopleListPm = null
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}

/**
 * Suggestions minus whoever is already there.
 *
 * Four surfaces needed the same two lines — map to emails, drop the ones already
 * granted or already a member — and a fifth would have copied them again.
 * Suggesting someone who is already in the team is suggesting a no-op; suggesting
 * someone who already holds a grant offers to *replace* it, which is not what
 * reaching for a suggestion means.
 */
export const suggestPeople = (people: readonly PersonPm[] | null, exclude: Iterable<string>): string[] => {
  const already = new Set(exclude)
  return (people ?? []).map((person) => person.email).filter((email) => !already.has(email))
}

/** The display name a loaded people list carries for an email, or null. */
export const displayNameOf = (people: readonly PersonPm[] | null, email: string): string | null =>
  (people ?? []).find((person) => person.email === email)?.displayName ?? null
